import FormField from '@components/Common/FormField';
import FilterRow from '@components/Common/FilterRow';
import PageHeader, { PageHeaderTabs } from '@components/Common/PageHeader';
import SavedViewsBar, { SavedView } from '@components/Common/SavedViewsBar';
import SearchFilter, { Column } from '@components/Common/SearchFilter/SearchFilter';
import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { Badge, Box, Button, Divider, IconButton, InputAdornment, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import './index.scss';

// Max content width — keeps dense tables readable on wide monitors
/*
 * Page geometry comes from the same custom properties every other page uses, declared in
 * styles/design-system.scss. This file used to carry its own numbers, so a table page capped at
 * 1400px with 32px gutters while the rest of the app capped at 1500px with 16px, and content on a
 * table started about 66px further from the edge than content anywhere else. That is the "tables
 * have extra left and right padding" everyone could see and nobody could point at.
 */
const MAX_CONTENT_WIDTH = 'var(--page-max-width)';
// Responsive on its own, via a media query on the property itself, so the two layouts narrow
// together rather than at different breakpoints.
const CONTENT_PX = 'var(--page-gutter-x)';
const SIDEBAR_WIDTH = 260; // desktop filter sidebar width in px

interface CRMPageTemplateProps {
  title: string;
  /** Optional one-line context beneath the title (11.5px, muted). */
  subtitle?: string;
  icon: React.ReactNode;
  totalCount?: number;
  filterLabel?: string;
  filterColumns?: string[];
  booleanColumns?: string[];
  /** Number-bound filter columns; see SearchFilter. */
  numberColumns?: string[];
  locationColumns?: string[];
  localityColumns?: string[];
  columnOptions?: Record<string, string[]>;
  /** Per-column display labels for option values whose stored form is not readable (e.g. true/false). */
  optionLabels?: Record<string, Record<string, string>>;
  initialColumns?: Column[];
  setFilterData?: React.Dispatch<React.SetStateAction<Column[]>>;
  /** `clearAll` is true when triggered by the filter panel's "Clear All" — page-owned
   *  extraFilterContent state must reset itself when it is set. */
  onApplyFilters?: (filters: Column[], clearAll?: boolean) => void;
  /**
   * Page-specific filter control rendered inside the filter panel, below the date range.
   * Use for filters that cannot be expressed as a filterColumn — i.e. where the displayed
   * label differs from the value sent to GraphQL (i.e. an id-backed dropdown).
   * The panel is staged-then-Apply, so keep the value pending and commit it in onApplyFilters.
   */
  extraFilterContent?: React.ReactNode;
  onAddNew?: () => void;
  addNewLabel?: string;
  /** Optional preset views over the existing filters (rendered below the header). */
  savedViews?: SavedView[];
  activeViewId?: string;
  onSelectView?: (id: string) => void;
  onSaveView?: () => void;
  children: React.ReactNode;
  locationColumnTypeMapping?: Record<string, string>;
  localityAutoFillColumns?: Record<string, { city?: string; state?: string; country?: string }>;
  /** Server-side search fetchers for high-cardinality filter columns (e.g. Company Name). */
  asyncSearch?: Record<string, (query: string) => Promise<string[]>>;
  /** Custom filter controls rendered ABOVE the standard filter columns (top of the panel). */
  topFilterContent?: React.ReactNode;
  /**
   * Always-visible controls rendered above the table, in the content column.
   *
   * NOT the filter panel. The panel is staged-then-Apply, which is right for server-side queries
   * and wrong for filtering a list that is already in memory — an Apply click to hide two rows
   * reads as the page being slow. PipelineBoard and Leads both hand-rolled such a row with inline
   * `sx`, which put two filter UIs on one page and two different ideas of where filters live.
   *
   * Use this when filtering is instant and client-side. Use `filterColumns` / `extraFilterContent`
   * when it triggers a query. Never both on one page.
   */
  inlineFilters?: React.ReactNode;
  dateFrom?: string;
  dateTo?: string;
  onDateFromChange?: (v: string) => void;
  onDateToChange?: (v: string) => void;
  /** The page's tabs, rendered directly under the header. Or provide PageTabsContext. */
  tabs?: PageHeaderTabs;
  /** Extra header actions (icon buttons), placed before the filter toggle and Add button. */
  headerActions?: React.ReactNode;
}

const CRMPageTemplate: React.FC<CRMPageTemplateProps> = ({
  title,
  subtitle,
  icon,
  totalCount,
  filterLabel = 'Filters',
  filterColumns,
  booleanColumns = [],
  numberColumns = [],
  locationColumns = [],
  localityColumns = [],
  columnOptions = {},
  optionLabels = {},
  asyncSearch = {},
  topFilterContent,
  inlineFilters,
  initialColumns,
  setFilterData,
  onApplyFilters,
  extraFilterContent,
  onAddNew,
  addNewLabel = 'Add New',
  savedViews,
  activeViewId,
  onSelectView,
  onSaveView,
  children,
  locationColumnTypeMapping = {},
  localityAutoFillColumns = {},
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  tabs,
  headerActions,
}) => {
  const hasDateFilter = !!(onDateFromChange && onDateToChange);
  const hasFilter = !!(filterColumns && setFilterData);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Today's date in UTC (YYYY-MM-DD) — used as max on date pickers to block future dates
  const todayUTC = new Date().toISOString().slice(0, 10);

  // Pending (staged) date values — only committed to parent on Apply
  const [pendingDateFrom, setPendingDateFrom] = useState(dateFrom ?? '');
  const [pendingDateTo, setPendingDateTo] = useState(dateTo ?? '');
  const [dateRangeError, setDateRangeError] = useState('');

  // Sync pending state if parent resets dates externally (e.g. clear-all)
  useEffect(() => { setPendingDateFrom(dateFrom ?? ''); }, [dateFrom]);
  useEffect(() => { setPendingDateTo(dateTo ?? ''); }, [dateTo]);

  // Intercepts the SearchFilter "Apply Filters" / "Clear All" click.
  // Validates date range (both or neither) before committing anything.
  // clearDates = true when triggered by "Clear All" — wipes date range regardless of pending values.
  const handleApply = (filters: Column[], clearDates = false) => {
    if (hasDateFilter) {
      const effectiveDateFrom = clearDates ? '' : pendingDateFrom;
      const effectiveDateTo = clearDates ? '' : pendingDateTo;
      const hasFrom = effectiveDateFrom.trim() !== '';
      const hasTo = effectiveDateTo.trim() !== '';
      if (hasFrom !== hasTo) {
        setDateRangeError('Both From and To are required, or clear both.');
        return;
      }
      setDateRangeError('');
      if (clearDates) {
        setPendingDateFrom('');
        setPendingDateTo('');
      }
      onDateFromChange!(hasFrom ? effectiveDateFrom : '');
      onDateToChange!(hasTo ? effectiveDateTo : '');
    }
    onApplyFilters?.(filters, clearDates);
  };

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try { return localStorage.getItem('crmFilterSidebarOpen') !== 'false'; } catch { return true; }
  });

  const toggleSidebar = () => {
    setSidebarOpen(prev => {
      const next = !prev;
      try { localStorage.setItem('crmFilterSidebarOpen', String(next)); } catch {}
      return next;
    });
  };

  const activeFilterCount = (initialColumns ?? []).filter(c => Boolean(c.searchValue)).length;

  // Contents of SearchFilter's extraContent slot: the date range plus any page-specific
  // control. Built once and passed to BOTH the desktop sidebar and the mobile drawer, so
  // these controls are not desktop-only.
  /*
   * The date range and the page's own control are FIELDS, not sections.
   *
   * They used to be two bordered blocks rendered after the scrolling body, which made three
   * problems at once on a phone: they read as separate panels, they sat outside the scroll area
   * so they permanently squeezed the column list above them, and the date pair carried its own
   * label spec ("From"/"To" in sentence case) against everything else in caps. They are ordinary
   * .sf-drawer-field rows now, and SearchFilter renders this slot inside the scroller, so the
   * whole panel is one list with a pinned footer.
   */
  const filterExtras =
    hasDateFilter || extraFilterContent ? (
      <>
        {hasDateFilter && (
          <>
          <Box className="sf-drawer-field">
            <Typography className="sf-drawer-field-label" variant="overline">
              Created from
            </Typography>
            {/* label="" because the row above is the label. FormField suppresses its own when the
                label is empty, which also drops the htmlFor association, so the accessible name
                is restored explicitly rather than left to the visual one. */}
            <FormField
              required={false}
              name="date-from"
              label=""
              type="date"
              value={pendingDateFrom}
              textFieldProps={{
                slotProps: {
                  input: pendingDateFrom ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" tabIndex={-1} edge="end" onClick={() => { setPendingDateFrom(''); setDateRangeError(''); }} sx={{ mr: -0.5 }}>
                          <ClearIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  } : undefined,
                  htmlInput: { max: todayUTC, 'aria-label': 'Created from' },
                },
              }}
              onChange={(_n, v) => {
                if (String(v) > todayUTC) return;
                setPendingDateFrom(String(v));
                setDateRangeError('');
              }}
            />
          </Box>
          <Box className="sf-drawer-field">
            <Typography className="sf-drawer-field-label" variant="overline">
              Created to
            </Typography>
            {/* label="" because the row above is the label. FormField suppresses its own when the
                label is empty, which also drops the htmlFor association, so the accessible name
                is restored explicitly rather than left to the visual one. */}
            <FormField
              required={false}
              name="date-to"
              label=""
              type="date"
              value={pendingDateTo}
              textFieldProps={{
                slotProps: {
                  input: pendingDateTo ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" tabIndex={-1} edge="end" onClick={() => { setPendingDateTo(''); setDateRangeError(''); }} sx={{ mr: -0.5 }}>
                          <ClearIcon sx={{ fontSize: 12 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  } : undefined,
                  htmlInput: { max: todayUTC, 'aria-label': 'Created to' },
                },
              }}
              onChange={(_n, v) => {
                if (String(v) > todayUTC) return;
                setPendingDateTo(String(v));
                setDateRangeError('');
              }}
            />
          </Box>
            {dateRangeError && (
              <Typography variant="caption" sx={{ color: 'error.main', fontSize: 'var(--font-size-3xs)', mt: -0.5 }}>
                {dateRangeError}
              </Typography>
            )}
          </>
        )}
        {extraFilterContent}
      </>
    ) : undefined;

  return (
    // `cc-crm` marks the root so the layout is identifiable from outside. EntityWorkspace has
    // `cc-ws` and EditorShell has `cc-ed`; this one had nothing, so of the three documented layouts
    // the CRM list was the only one a page could *appear* to use without using it. The e2e smoke
    // matrix asserts a route renders the shell its manifest entry claims, which is only possible
    // once all three say what they are. Carries no styles — sx still owns the layout.
    <Box
      className="cc-crm"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* ── Page header: icon, H1, count, actions, then the page's tabs ── */}
      <PageHeader
        title={title}
        icon={icon}
        count={totalCount}
        subtitle={subtitle}
        tabs={tabs}
        actions={(headerActions || onAddNew || (hasFilter && isDesktop)) ? (
          <>
            {headerActions}
            {hasFilter && isDesktop && (
              <Tooltip title={sidebarOpen ? 'Hide filters' : 'Show filters'}>
                {/* Size comes from the IconButton spec. It used to be
                    size="small" with width and height hardcoded to 34,
                    which was neither of the two sizes. Only the on/off
                    colouring is this component's business. */}
                <IconButton
                  aria-label={sidebarOpen ? 'Hide filters' : 'Show filters'}
                  onClick={toggleSidebar}
                  sx={{
                    color: sidebarOpen ? 'var(--acc)' : 'var(--tx-mid)',
                    backgroundColor: sidebarOpen ? 'var(--acc-tint)' : 'transparent',
                    border: '1px solid',
                    borderColor: sidebarOpen ? 'var(--acc-line)' : 'var(--line)',
                    '&:hover': { backgroundColor: 'var(--acc-tint)' },
                  }}
                >
                  <Badge
                    badgeContent={activeFilterCount}
                    color="primary"
                    sx={{ '& .MuiBadge-badge': { fontSize: 'var(--font-size-3xs)', height: 16, minWidth: 16 } }}
                  >
                    {sidebarOpen ? <FilterListOffIcon /> : <FilterListIcon />}
                  </Badge>
                </IconButton>
              </Tooltip>
            )}
            {onAddNew && (
              <Button
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                variant="contained"
                onClick={onAddNew}
                sx={{ flexShrink: 0 }}
              >
                {addNewLabel}
              </Button>
            )}
          </>
        ) : undefined}
        below={hasFilter && !isDesktop ? (
          /* Filter toolbar: only on mobile (desktop uses the LHS sidebar) */
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pb: 1.5,
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <SearchFilter
              booleanColumns={booleanColumns}
              numberColumns={numberColumns}
              columns={filterColumns!}
              columnOptions={columnOptions}
              optionLabels={optionLabels}
              asyncSearch={asyncSearch}
              topContent={topFilterContent}
              initialColumns={initialColumns}
              label={filterLabel}
              locationColumns={locationColumns}
              locationColumnTypeMapping={locationColumnTypeMapping}
              localityColumns={localityColumns}
              localityAutoFillColumns={localityAutoFillColumns}
              setFilterData={setFilterData!}
              onApply={handleApply}
              extraContent={filterExtras}
            />
          </Box>
        ) : undefined}
      />

      {/* ── Saved views bar (optional, presets over existing filters) ── */}
      {savedViews && savedViews.length > 0 && (
        <SavedViewsBar
          views={savedViews}
          activeViewId={activeViewId ?? ''}
          onSelect={onSelectView ?? (() => {})}
          onSaveCurrent={onSaveView}
        />
      )}

      <Divider />

      {/* ── Body area ── */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          // A size container, so the filter panel can be capped at this scroller's height (cqh)
          // and keep its Apply button on screen. Only with the desktop panel: every page that has
          // one sits in a definite-height parent, while a page without one (HR's wrapper) may
          // not, and size containment would collapse its scroller to nothing.
          ...(hasFilter && isDesktop ? { containerType: 'size' } : {}),
        }}
      >
        {/* Width cap + horizontal padding mirrors header exactly */}
        <Box
          sx={{
            maxWidth: MAX_CONTENT_WIDTH,
            mx: 'auto',
            px: CONTENT_PX,
            // One 16px gap under the header at every width, as on the dashboards; it was 24px here
            // from a tablet up, so a list page sat 8px lower under its tabs than a dashboard did.
            pt: 2,
            /*
             * The bottom gutter comes from the token, not from `py`.
             *
             * `py: {xs: 2}` gave the phone 16px, and on a scrolled list that 16px is the only
             * thing under the pagination -- so the last control on the page sat 17px from the
             * edge of its own scroller and read as having no bottom spacing at all. Desktop was
             * already 24px via `md: 3`, and --page-gutter-bottom is the same 24px, which is what
             * the bar clearance in MobileNav is built from. There is one page gutter; the phone
             * was simply not using it.
             */
            pb: 'var(--page-gutter-bottom)',
            display: 'flex',
            gap: 0,
            alignItems: 'flex-start',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Desktop filter sidebar — collapsible, slides in/out with CSS width transition */}
          {hasFilter && isDesktop && (
            <Box
              component="aside"
              sx={{
                width: sidebarOpen ? SIDEBAR_WIDTH : 0,
                // The one 16px section gap; it was 24px here and 16px everywhere else.
                marginRight: sidebarOpen ? 2 : 0,
                flexShrink: 0,
                overflow: 'hidden',
                transition: 'width 0.2s ease, margin-right 0.2s ease',
                /*
                 * Pinned, and no taller than the scroller. The panel's Apply button sat at the
                 * bottom of a column as tall as its fields, so on Jobs, Job Seekers and
                 * Applications it was below the fold: those pages looked like they filtered live
                 * while Companies and Subscriptions, with short panels, showed the button. Same
                 * component, three apparent behaviours. Now the fields scroll inside the panel and
                 * the footer is always in view.
                 */
                position: 'sticky',
                top: 'var(--spacing-lg)',
                alignSelf: 'flex-start',
              }}
            >
              <Box
                sx={{
                  width: SIDEBAR_WIDTH,
                  maxHeight: 'calc(100cqh - var(--spacing-lg) - var(--page-gutter-bottom))',
                  display: 'flex',
                  flexDirection: 'column',
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: 'var(--shadow-sm)',
                }}
              >
                <SearchFilter
                  sidebarMode
                  booleanColumns={booleanColumns}
                  numberColumns={numberColumns}
                  columns={filterColumns!}
                  columnOptions={columnOptions}
                  optionLabels={optionLabels}
                  asyncSearch={asyncSearch}
                  topContent={topFilterContent}
                  initialColumns={initialColumns}
                  label={filterLabel}
                  locationColumns={locationColumns}
                  locationColumnTypeMapping={locationColumnTypeMapping}
                  localityColumns={localityColumns}
                  localityAutoFillColumns={localityAutoFillColumns}
                  setFilterData={setFilterData!}
                  onApply={handleApply}
                  extraContent={filterExtras}
                />
              </Box>
            </Box>
          )}

          {/* Main content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {inlineFilters && <FilterRow>{inlineFilters}</FilterRow>}
            {children}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CRMPageTemplate;
